import { Box, Checkbox, InputAdornment, Link, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { useFormContext } from "react-hook-form"
import { IFiltrosSelecionarProduto, InputsSelecionarProduto } from ".."
import { calcularDiferencaEmMeses } from "../../../../helpers/calcularDiferencaEmMeses"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import axiosClient from "../../../../lib/axios"
import LoadingApiQuery from "../../../LoadingApiQuery"
import DisplayNotFoundInfo from "../../../DisplayNotFindInfo"
import SazonalidadeChip from "../SazonalidadeChip"
import DialogEncomendas from "./DialogEncomendas"
import DialogObservacoes from "./DialogObservacoes"
import { useModalDetalheProduto } from "../../../../screens/Estoque/components/MIProdutos/ModalDetalheProdutoContext"

export default function TableProdutos({
    isRessuprimento = false
}: TableProdutosProps) {

    const { watch, setValue, getValues } = useFormContext<InputsSelecionarProduto>()
    const { openModal: openDetalheProduto } = useModalDetalheProduto();
    const filters: IFiltrosSelecionarProduto = watch('filters')
    const selecionados: InputProduto[] = watch('items')

    const modalDefaultValue = useMemo(() => ({
        modalState: false,
        data: null
    }), [])

    const [focusRow, setFocusRow] = useState<number>(-1)
    const [fetchData, setFetchData] = useState(false)
    const [openModal, setOpenModal] = useState<TOpenModalEncomendas>(modalDefaultValue)

    // Querys e mutations
    const { data, isFetching } = useQuery({
        queryKey: ["pesquisar-produtos-origem", filters],
        queryFn: () => axiosClient.get("/estoque/pesquisar-produtos-origem", { params: transformFilters() }),
        refetchOnWindowFocus: false,
        enabled: fetchData,
        cacheTime: 0
    });

    const previsao: TPrevisao = data?.data;

    // Funções Manipuladoras
    const handleOpenModalEncomendas = useCallback((action: "open" | "close", value?: TOpenModalEncomendas | null) => {
        if (action === "open" && value) setOpenModal(value)
        if (action === "close") setOpenModal(modalDefaultValue)
    }, [modalDefaultValue])

    const handleChange = (codigo: number, nome: string, checkbox: boolean, quantidade: number = 0, departamento: string, setor: string, sugerido: boolean, estoque_total: number, unidade_medida: string) => {
        let newSelecionados: Array<InputProduto> = [...selecionados];
        const params = {
            codigo,
            nome,
            quantidade,
            encomenda: null,
            observacao: null,
            departamento,
            setor,
            estoque_total,
            unidade_medida,
            nao_sugerido: false
        }

        if (!checkbox) {
            newSelecionados = newSelecionados.filter((i) => i.codigo !== codigo);
        }

        if (checkbox) {
            let index = newSelecionados.findIndex((i) => i.codigo === codigo);
            if (index < 0) {
                newSelecionados.push(params)
            } else {
                newSelecionados[index] = params
            }
        }

        setValue('items', newSelecionados);
    }

    const handleFocus = (rowIndex: number) => {
        setFocusRow(rowIndex);
    };

    const transformFilters = useCallback(() => {
        const { predSelecionado, origem, destino, ...filterFormatado } = filters;

        const transformedFilters = {
            ...filterFormatado,
            pesquisar: filters.palavra_chave,
            destino_loja_id: destino?.id || null,
            periodo_vendas_inicio: filters.periodo_vendas_inicio.format('YYYY-MM-DD'),
            periodo_vendas_fim: filters.periodo_vendas_fim.format('YYYY-MM-DD'),
            sazonalidade_inicio: filters.sazonalidade_inicio.format('YYYY-MM-DD'),
            sazonalidade_fim: filters.sazonalidade_fim.format('YYYY-MM-DD'),
            departamentos: filters.departamentos?.map(i => (i.id))
        };

        return transformedFilters;
    }, [filters]);

    useEffect(() => {
        let filledFields = filters.destino ? true : false
        if (isRessuprimento) {
            filledFields = filledFields && filters.origem ? true : false
        }
        if (filledFields) setFetchData(true)
    }, [filters.destino, filters.origem, isRessuprimento])

    return (
        <>
            {
                isFetching &&
                <LoadingApiQuery />
            }
            {
                !isFetching && (!previsao || previsao?.data.length === 0) &&
                <DisplayNotFoundInfo label="Nenhum produto encontrado." />
            }
            {!isFetching && previsao && previsao.data.length > 0 &&
                <>
                    <Table size="small" sx={{ maxHeight: 500, overflow: 'auto', mt: 2 }}>
                        <TableHead sx={{
                            position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 999,
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                        }}>
                            <TableRow>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>
                                    <div style={{ marginTop: 8 }}>Produto</div>
                                    <Typography variant="caption">Departamento / Setor</Typography>
                                </TableCell>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: 0 }}>Qtd. em estoque</TableCell>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: 0 }}>Qtd. total de vendas</TableCell>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} width={150} align='center'>
                                    <div style={{ marginTop: 8 }}>Estoque (dias)</div>
                                </TableCell>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} align='center'>Sugestão para {filters.qtdSuggestion} dia(s)</TableCell>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} width={150} align='center'>Solicitar</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    {
                                        isRessuprimento &&
                                        <>
                                            <Typography component="div" variant="caption">Origem</Typography>
                                            <Typography component="div" variant="caption">{filters.origem?.nome ?? '-'}</Typography>
                                        </>
                                    }
                                    {
                                        !isRessuprimento &&
                                        <Typography component="div" variant="caption">Total</Typography>
                                    }
                                </TableCell>
                                {
                                    isRessuprimento &&
                                    <TableCell>
                                        <Typography component="div" variant="caption">Destino</Typography>
                                    </TableCell>
                                }
                                {
                                    !isRessuprimento &&
                                    <TableCell>
                                        {
                                            !getValues('filters.calcular_nivel_global') &&
                                            <Typography component="div" variant="caption">{filters.destino?.nome ?? '-'}</Typography>
                                        }
                                    </TableCell>
                                }
                                <TableCell sx={{ minWidth: 90 }}>
                                    <Typography component="div" variant="caption">Período</Typography>
                                    <Typography component="div" variant="caption">{calcularDiferencaEmMeses(transformFilters().periodo_vendas_inicio, transformFilters().periodo_vendas_fim)} meses</Typography>
                                </TableCell>
                                <TableCell sx={{ minWidth: 82 }}>
                                    <Typography component="div" variant="caption">Sazonal</Typography>
                                    <Typography component="div" variant="caption">{calcularDiferencaEmMeses(transformFilters().sazonalidade_inicio, transformFilters().sazonalidade_fim)} meses</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                previsao?.data.map((row: TProdutosRessuprimento, index: number) => {
                                    const existeRecomendacao = !Boolean(row.nao_recomendado)
                                    let selecionado = selecionados.find(({ codigo }) => codigo === row.codigo);
                                    let encomendasProp: TOpenModalEncomendas = modalDefaultValue;
                                    let qtdVendasDiaArredondado = Math.ceil(row.qtd_vendas_media_diaria * 100) / 100;
                                    let qtdVendasSazonalDiaArredondado = Math.ceil(row.qtd_vendas_sazonal_media_diaria * 100) / 100;

                                    if (selecionado && selecionado.quantidade > 0) {
                                        encomendasProp = {
                                            modalState: true,
                                            data: {
                                                ...selecionado,
                                            },
                                        };
                                    }

                                    let nivelColor = "#ccc"
                                    if (row.nivel >= 0 && row.nivel <= 15) nivelColor = '#ff8a8a'
                                    if (row.nivel > 15 && row.nivel <= 30) nivelColor = '#FEC1C1'
                                    if (row.nivel > 30 && row.nivel <= 60) nivelColor = '#f9e785'
                                    if (row.nivel > 60) nivelColor = '#E1FEE0'

                                    return (
                                        <TableRow key={row.codigo} sx={{ backgroundColor: focusRow === index ? 'rgb(237, 247, 237)' : 'none' }}>
                                            <TableCell>
                                                <Typography>
                                                    <Link
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => openDetalheProduto(String(row.codigo))}
                                                    >
                                                        {row.codigo}
                                                    </Link> - {row.nome}</Typography>
                                                <Typography variant="caption">{row.departamento} / {row.setor}</Typography>
                                                <Stack direction="row" spacing={0.2}>
                                                    {
                                                        row.sazonalidades.map((item: any, index) =>
                                                        (
                                                            <SazonalidadeChip key={index} nome={item.nome} cores={item.cores} />
                                                        ))
                                                    }
                                                </Stack>
                                            </TableCell>

                                            <TableCell>{isRessuprimento ? row.estoque_origem : row.estoque_total}</TableCell>

                                            {!isRessuprimento && getValues('filters.calcular_nivel_global') ? (
                                                <TableCell></TableCell>
                                            ) : <TableCell>{row.estoque_atual}</TableCell>}

                                            <TableCell>
                                                <Tooltip title={`${qtdVendasDiaArredondado} por dia`}>
                                                    <Box>{row.qtd_vendas}</Box>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell>
                                                <Tooltip title={`${qtdVendasSazonalDiaArredondado} por dia`}>
                                                    <Box>{row.qtd_vendas_sazonal}</Box>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={{ backgroundColor: nivelColor }}>
                                                <Typography variant="body2">{existeRecomendacao ? `${row.nivel} dia(s)` : "-"}</Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                {
                                                    existeRecomendacao ?
                                                        Math.ceil((
                                                            qtdVendasDiaArredondado > qtdVendasSazonalDiaArredondado ? qtdVendasDiaArredondado : qtdVendasSazonalDiaArredondado)
                                                            * filters.qtdSuggestion
                                                        ) : "Não sugerido"
                                                }
                                            </TableCell>

                                            <TableCell>
                                                <div style={{ display: 'flex' }}>
                                                    <Checkbox
                                                        checked={selecionado ? true : false}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            handleChange(row.codigo, row.nome, event.target.checked, 0, row.departamento, row.setor, row.sugerido, row.estoque_origem, row.unidade_medida);
                                                        }}
                                                    />
                                                    <Box display={'flex'} flexDirection={'column'}>
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            error={selecionado && selecionado.quantidade <= 0 ? true : false}
                                                            value={selecionado?.quantidade || ''}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                handleChange(row.codigo, row.nome, true, parseInt(event.target.value,) || 0, row.departamento, row.setor, row.sugerido, row.estoque_total, row.unidade_medida);
                                                            }}
                                                            onFocus={() => handleFocus(index)}
                                                            onBlur={() => handleFocus(-1)}
                                                            slotProps={{
                                                                htmlInput: { tabIndex: index + 1 },
                                                                inputLabel: { shrink: selecionado ? true : false },
                                                                input: { endAdornment: <InputAdornment position="end">{row.unidade_medida}</InputAdornment> }
                                                            }}
                                                            sx={{
                                                                minWidth: 100
                                                            }}
                                                        />
                                                        {
                                                            selecionado && !isRessuprimento &&
                                                            <Box marginTop={1} width={'100%'} display={'flex'} justifyContent={'flex-end'}>
                                                                <Link component="button" fontSize={12} textAlign={'right'} justifyContent={"flex-end"}
                                                                    onClick={() => handleOpenModalEncomendas('open', encomendasProp ? encomendasProp : null)}
                                                                >
                                                                    Observação
                                                                </Link>
                                                            </Box>
                                                        }
                                                        {
                                                            selecionado && !selecionado.encomenda && isRessuprimento &&

                                                            <Box marginTop={1} width={'100%'} display={'flex'} justifyContent={'flex-end'}>
                                                                <Link component="button" fontSize={12} textAlign={'right'} justifyContent={"flex-end"}
                                                                    onClick={() => handleOpenModalEncomendas('open', encomendasProp ? encomendasProp : null)}
                                                                >
                                                                    É encomenda?
                                                                </Link>
                                                            </Box>

                                                        }
                                                        {
                                                            selecionado && selecionado.encomenda &&

                                                            <Box marginTop={1} width={'100%'}>
                                                                <Link width={'100%'} component="button" fontSize={11} textAlign={'right'} justifyContent={"flex-end"}
                                                                    onClick={() => handleOpenModalEncomendas('open', encomendasProp ? encomendasProp : null)}
                                                                >Encomenda: {selecionado.encomenda} {row.unidade_medida}
                                                                </Link>
                                                            </Box>

                                                        }
                                                        {
                                                            openModal.modalState && openModal.data?.codigo === selecionado?.codigo && isRessuprimento &&
                                                            <DialogEncomendas
                                                                {...{ selecionados }}
                                                                onChange={(itens) => setValue('items', itens)}
                                                                open={openModal.modalState}
                                                                row={row}
                                                                selecionado={openModal.data}
                                                                handleClose={() => handleOpenModalEncomendas("close")}
                                                            />
                                                        }
                                                        {
                                                            openModal.modalState && openModal.data?.codigo === selecionado?.codigo && !isRessuprimento &&
                                                            <DialogObservacoes
                                                                {...{ selecionados }}
                                                                onChange={(itens) => setValue('items', itens)}
                                                                open={openModal.modalState}
                                                                row={row}
                                                                selecionado={openModal.data}
                                                                handleClose={() => handleOpenModalEncomendas("close")}
                                                            />
                                                        }
                                                    </Box>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            }

                            {
                                previsao?.total === 0 &&
                                <TableRow>
                                    <TableCell colSpan={9}>Nenhum item para exibir</TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={previsao?.total}
                        page={filters.page}
                        rowsPerPage={filters.limit}
                        onPageChange={(
                            event: React.MouseEvent<HTMLButtonElement> | null,
                            newPage: number
                        ) => {
                            setValue('filters', { ...filters, page: newPage })
                        }}
                        onRowsPerPageChange={(
                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                        ) => {
                            setValue('filters', { ...filters, page: 0, limit: parseInt(event.target.value, 10) })
                        }}
                    />
                </>

            }
        </>
    )
}

interface TableProdutosProps {
    isRessuprimento?: boolean
}

type TPrevisao = {
    total: number;
    data: Array<TProdutosRessuprimento>;
    per_page: number;
    current_page: number;
}

export type TProdutosRessuprimento = {
    codigo: number;
    departamento: string;
    departamento_id: string;
    estoque_atual: number;
    estoque_origem: number;
    estoque_total: number;
    nivel: number;
    nome: string;
    qtd_vendas: number;
    qtd_vendas_dias_suporta: number;
    qtd_vendas_media_diaria: number;
    qtd_vendas_media_mensal: number;
    qtd_vendas_sazonal: number;
    qtd_vendas_sazonal_dias_suporta: number | null;
    qtd_vendas_sazonal_media_diaria: number;
    qtd_vendas_sazonal_media_mensal: number;
    sazonalidades: [{ nome: string, cores: string, inicio: string, fim: string }];
    setor: string;
    setor_id: string;
    sugerido: boolean;
    unidade_medida: string;
    nao_recomendado: number;
}

export type InputProduto = {
    codigo: number,
    nome: string,
    quantidade: number,
    departamento: string,
    setor: string,
    encomenda: number | null,
    nao_sugerido?: boolean
    observacao: string | null
    estoque_total: number
    unidade_medida: string
}

export type TOpenModalEncomendas = {
    modalState: boolean,
    data: InputProduto | null
}